import { useState, useEffect } from "react";
import { ChevronRight, Brain, Clock, BookOpen, Shield, Calendar, CheckCircle } from "lucide-react";

export default function MederaCollage() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Animation classes
  const fadeIn = isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10";

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-8 md:py-12 bg-gradient-to-br from-white to-blue-50">
      {/* Header Section */}
      <div className={`text-center mb-8 transition-all duration-500 ${fadeIn}`}>
        <h1 className="text-3xl md:text-5xl font-bold text-gray-800 mb-3">
          Introducing MEDERA: The Future of NEET-PG Preparation
        </h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Finally Here – The Revolutionary New App That Transforms How You Study for NEET-PG!
        </p>
      </div>

      {/* Collage Layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {/* Main Feature - Large Card */}
        <div className={`bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl shadow-lg p-6 col-span-1 md:col-span-2 transition-all duration-500 ${fadeIn}`}>
          <div className="flex flex-col h-full justify-between">
            <div>
              <Brain size={40} className="mb-4" />
              <h2 className="text-2xl font-bold mb-3">Complete NEET-PG Preparation</h2>
              <p className="text-lg opacity-90 mb-6">
                MEDERA combines evidence-based learning techniques with comprehensive NEET-PG content curated by top medical educators and previous toppers.
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <div className="flex items-center">
                <CheckCircle size={20} className="mr-2 flex-shrink-0" />
                <span>19 Clinical Subjects</span>
              </div>
              <div className="flex items-center">
                <CheckCircle size={20} className="mr-2 flex-shrink-0" />
                <span>6,000+ Flashcards</span>
              </div>
              <div className="flex items-center">
                <CheckCircle size={20} className="mr-2 flex-shrink-0" />
                <span>Previous Year MCQs</span>
              </div>
              <div className="flex items-center">
                <CheckCircle size={20} className="mr-2 flex-shrink-0" />
                <span>Custom Study Plans</span>
              </div>
            </div>
          </div>
        </div>

                  {/* App Mockup */}
        <div className={`bg-rose-100 rounded-2xl shadow-md overflow-hidden col-span-1 transition-all duration-500 ${fadeIn}`}>
          <div className="p-4 h-full flex flex-col">
            <div className="bg-white rounded-xl p-4 shadow-sm flex-1">
              <div className="text-center mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-lg mx-auto mb-2">M</div>
                <h4 className="font-medium text-gray-800">MEDERA</h4>
                <p className="text-xs text-gray-500">Your NEET-PG Success Partner</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-center font-medium text-gray-900 mb-3">Experience Smarter Learning</p>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-500">12,000+ Flashcards</span>
                  <span className="text-blue-500">19 Subjects</span>
                </div>
              </div>
              
              <button className="bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg w-full py-2 flex items-center justify-center">
                Get Started Now <ChevronRight size={16} className="ml-1" />
              </button>
              
              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-2">Join these medical students</p>
                <div className="flex">
                  <div className="flex -space-x-2">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className={`w-6 h-6 rounded-full bg-gradient-to-r from-blue-${300 + i*100} to-blue-${400 + i*100} border border-white`}></div>
                    ))}
                  </div>
                  <span className="text-xs ml-2 text-gray-600 flex items-center">2,400+ users</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Card 1 */}
        <div className={`bg-white rounded-2xl shadow-md p-5 transition-all duration-500 ${fadeIn}`}>
          <div className="text-emerald-500 mb-3">
            <BookOpen size={28} />
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">Revolutionary Learning Approach</h3>
          <p className="text-sm text-gray-600">
            Breaking away from outdated study methods, the newly launched MEDERA app harnesses cognitive science with active recall and spaced repetition to enhance memory retention by up to 50%.
          </p>
        </div>

        {/* Feature Card 2 */}
        <div className={`bg-white rounded-2xl shadow-md p-5 transition-all duration-500 ${fadeIn}`}>
          <div className="text-amber-500 mb-3">
            <Clock size={28} />
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">Smart Daily Study Modules</h3>
          <p className="text-sm text-gray-600">
            The innovative MEDERA platform delivers concise daily doses of High Yield Topics, expertly curated by recent NEET-PG rank holders for this first-of-its-kind medical prep app.
          </p>
        </div>

        {/* Feature Card 3 */}
        <div className={`bg-white rounded-2xl shadow-md p-5 transition-all duration-500 ${fadeIn}`}>
          <div className="text-blue-500 mb-3">
            <Shield size={28} />
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">All-In-One NEET-PG Resource</h3>
          <p className="text-sm text-gray-600">
            Introducing MEDERA - the latest breakthrough app that consolidates essential NEET-PG content (including BTR + More) into digestible, quick-review flashcards in a single integrated platform.
          </p>
        </div>

        {/* Feature Card 4 */}
        <div className={`bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl shadow-md p-5 col-span-1 md:col-span-2 transition-all duration-500 ${fadeIn}`}>
          <div className="mb-3">
            <Calendar size={28} />
          </div>
          <h3 className="text-lg font-bold mb-2">Study Anytime, Anywhere</h3>
          <p className="text-sm opacity-90">
            With MEDERA, you can master a topic in just minutes. Revise an important topic even with 5 minutes to spare during hospital rounds, between patients, or on your commute.
          </p>
          <button className="mt-4 bg-white text-indigo-600 rounded-lg px-4 py-2 text-sm font-medium hover:bg-opacity-90 transition-all">
            Start Free Trial
          </button>
        </div>

        {/* Download Section */}
        <div className={`bg-gray-900 text-white rounded-2xl shadow-lg p-5 transition-all duration-500 ${fadeIn}`}>
          <h3 className="text-lg font-bold mb-2">Ready to Excel in NEET-PG?</h3>
          <p className="text-sm text-gray-300 mb-4">
            Join thousands of medical graduates who have transformed their NEET-PG preparation with MEDERA.
          </p>
          <button className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-lg py-2 text-sm font-medium transition-all">
            Download MEDERA
          </button>
          <p className="mt-3 text-xs text-center text-gray-400">Available on iOS and Android</p>
        </div>
      </div>
    </div>
  );
}